import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'src/app/hooks';
import useReceiptLineItemColumns from 'src/assets/static/new_receipt/receiptLineItemColumns';
import GridWrapper from 'src/components/GridWrapper';
import { fetchReceiptLineItemsData } from 'src/features/LineItemsGridSlice';

import { getter, process, State } from '@progress/kendo-data-query';
import {
  getSelectedState,
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridRowDoubleClickEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import uuid from 'react-uuid';
import InvoiceGridToolbar from 'src/sections/enter_invoice/InvoiceGridToolBar';
import { ReceiptLineItem } from 'src/services/useReceiptLineItemsApi';

const DATA_ITEM_KEY: string = 'rowId';
const idGetter = getter(DATA_ITEM_KEY);
const GridDataState: State = {
  sort: [],
  skip: 0,
  take: 10,
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: 'Line Items',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface IReceiptLineItemWizardProps {
  title: string;
  receiptId: number;
}

const ReceiptLineItemWizard: React.FC<IReceiptLineItemWizardProps> = ({ title, receiptId }) => {
  const dispatch = useAppDispatch();
  const [dataState, setDataState] = useState(GridDataState);
  const [lineItemData, setLineItemData] = useState<ReceiptLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const receiptLineItemColumns = useReceiptLineItemColumns();
  const [selectedRow, setSelectedRow] = useState<{
    [id: string]: boolean | number[];
  }>({});

  const { data, total } = useMemo(
    () => process(lineItemData, dataState),
    [dataState, lineItemData]
  );

  const getReceiptListItem = useCallback(
    () => dispatch(fetchReceiptLineItemsData(receiptId)),
    [dispatch, receiptId]
  );

  useEffect(() => {
    setLoading(true);
    getReceiptListItem()
      .unwrap()
      .then((res) => {
        const data = res.map((item: ReceiptLineItem[]) => ({ ...item, rowId: uuid() }));
        setLineItemData(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getReceiptListItem]);

  const onLineDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };
  const onSelectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedRow,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedRow(newSelectedState);
      setLineItemData((prev) => {
        const selectedList = prev.map((item) => ({
          ...item,
          selected: newSelectedState[idGetter(item)] as boolean,
        }));
        return selectedList;
      });
    },
    [selectedRow]
  );

  // clear filter
  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...GridDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  const onRowDoubleClick = (event: GridRowDoubleClickEvent) => {
    const key = event.dataItem[DATA_ITEM_KEY];
    setLineItemData((prev) => {
      const lineItems = [...prev].map((item) => {
        if (item.rowId === key) {
          return { ...item, selected: false };
        } else return item;
      });
      return lineItems;
    });
  };
  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  return (
    <>
      <p className="font-bold text-sm mt-2 mb-2">{title}</p>
      <GridWrapper
        id="receiptLineItemGrid"
        columns={receiptLineItemColumns}
        dataSource={data}
        dataState={dataState}
        height={'75vh'}
        total={total}
        onDataStateChange={onLineDataStateChange}
        {...defaultProps}
        onSelectionChange={onSelectionChange}
        loading={loading}
        gridToolbarChildren={
          <InvoiceGridToolbar
            clearFilter={handleClearFilter}
            isClearFiltersDisabled={isClearFiltersDisabled}
          />
        }
        onRowDoubleClick={onRowDoubleClick}
      />
    </>
  );
};
export default ReceiptLineItemWizard;
